import React from "react";
import AdminNavbar from "./AdminNavbar";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grow,
    IconButton,
    Paper,
    Radio,
    RadioGroup,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import RefreshIcon from "@mui/icons-material/Refresh";
import AdminData from "../../backend/dataservices/AdminData";

function AdminEmployeesPage() {

    const [employees, setEmployees] = React.useState([]);
    const [tenants, setTenants] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [search, setSearch] = React.useState('');
    const [hideAssigned, setHideAssigned] = React.useState(false);
    const [orderBy, setOrderBy] = React.useState('M_Name');
    const [order, setOrder] = React.useState('asc');
    const [page, setPage] = React.useState(0);
    const [editEmployee, setEditEmployee] = React.useState(null);
    const [selectedTenantId, setSelectedTenantId] = React.useState('none');
    const [saving, setSaving] = React.useState(false);
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarText, setSnackbarText] = React.useState("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");

    function loadEmployees() {
        setLoading(true);
        AdminData.getEmployees().then(data => {
            setEmployees(Array.isArray(data) ? data : []);
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    }

    React.useEffect(() => {
        loadEmployees();
        AdminData.getTenants().then(data => {
            if (Array.isArray(data)) setTenants(data);
        }).catch(() => {});
    }, []);

    function handleSort(column) {
        if (orderBy === column) {
            setOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(column);
            setOrder('asc');
        }
        setPage(0);
    }

    function getValue(employee, column) {
        switch (column) {
            case 'M_Name': return (employee.M_Vorname + ' ' + employee.M_Name).toLowerCase();
            case 'M_Pers-Nr': return (employee['M_Pers-Nr'] || '').toLowerCase();
            case 'TenantName': return (employee.TenantName || '').toLowerCase();
            case 'MigrationTenantName': return (employee.MigrationTenantName || '').toLowerCase();
            default: return '';
        }
    }

    const filtered = employees
        .filter(e => {
            if (hideAssigned && e.M_MigrationTenantID != null) return false;
            if (!search) return true;
            const s = search.toLowerCase();
            return (e.M_Name || '').toLowerCase().includes(s)
                || (e.M_Vorname || '').toLowerCase().includes(s)
                || (e['M_Pers-Nr'] || '').toLowerCase().includes(s);
        })
        .sort((a, b) => {
            const aVal = getValue(a, orderBy);
            const bVal = getValue(b, orderBy);
            if (aVal < bVal) return order === 'asc' ? -1 : 1;
            if (aVal > bVal) return order === 'asc' ? 1 : -1;
            return 0;
        });

    const paged = filtered.slice(page * 50, page * 50 + 50);

    function handleEditClick(employee) {
        setEditEmployee(employee);
        setSelectedTenantId(employee.M_MigrationTenantID != null ? String(employee.M_MigrationTenantID) : 'none');
    }

    function handleDialogClose() {
        setEditEmployee(null);
        setSelectedTenantId('none');
    }

    function handleSave() {
        if (!editEmployee) return;
        setSaving(true);
        const tenantId = selectedTenantId === 'none' ? null : parseInt(selectedTenantId, 10);
        AdminData.setMigrationTenant(editEmployee.M_ID, tenantId).then(() => {
            const tenantName = tenantId != null ? (tenants.find(t => t.T_ID === tenantId)?.T_Name || null) : null;
            setEmployees(prev => prev.map(e =>
                e.M_ID === editEmployee.M_ID
                    ? { ...e, M_MigrationTenantID: tenantId, MigrationTenantName: tenantName }
                    : e
            ));
            setSnackbarSeverity('success');
            setSnackbarText('Migration-Tenant zugewiesen.');
            setSnackbarOpen(true);
            handleDialogClose();
        }).catch(() => {
            setSnackbarSeverity('error');
            setSnackbarText('Fehler beim Zuweisen des Migration-Tenants.');
            setSnackbarOpen(true);
        }).finally(() => {
            setSaving(false);
        });
    }

    const columns = [
        { id: 'M_Name', label: 'Name' },
        { id: 'M_Pers-Nr', label: 'GID' },
        { id: 'TenantName', label: 'Tenant' },
        { id: 'MigrationTenantName', label: 'Migration Tenant' },
    ];

    return (
        <div>
            <AdminNavbar initValue={"/admin/employees"}/>
            <Box sx={{padding: '20px'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, marginBottom: '16px'}}>
                    <Typography variant={"h6"}>Mitarbeiter-Migration</Typography>
                    <Tooltip title={"Aktualisieren"}>
                        <IconButton size={"small"} onClick={loadEmployees} disabled={loading}>
                            <RefreshIcon fontSize={"small"}/>
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2, marginBottom: '16px'}}>
                    <TextField
                        size="small"
                        placeholder="Suche nach Name oder GID..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        sx={{width: '300px'}}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={hideAssigned}
                                onChange={(e) => { setHideAssigned(e.target.checked); setPage(0); }}
                                size="small"
                            />
                        }
                        label="Bereits zugewiesene ausblenden"
                    />
                </Box>
                {loading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', marginTop: '40px'}}>
                        <CircularProgress/>
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={3}>
                        <Table size={"small"}>
                            <TableHead>
                                <TableRow sx={{'& th': {fontWeight: 'bold', backgroundColor: '#f5f5f5'}}}>
                                    {columns.map(col => (
                                        <TableCell key={col.id}>
                                            <TableSortLabel
                                                active={orderBy === col.id}
                                                direction={orderBy === col.id ? order : 'asc'}
                                                onClick={() => handleSort(col.id)}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                    <TableCell align={"center"}>Aktion</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paged.map(employee => (
                                    <TableRow key={employee.M_ID} hover>
                                        <TableCell>{employee.M_Vorname} {employee.M_Name}</TableCell>
                                        <TableCell>
                                            {employee['M_Pers-Nr'] ? (
                                                <a
                                                    href={`https://scd.siemens.cloud/de/person/${employee['M_Pers-Nr']}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {employee['M_Pers-Nr']}
                                                </a>
                                            ) : ''}
                                        </TableCell>
                                        <TableCell>{employee.TenantName ?? '—'}</TableCell>
                                        <TableCell>{employee.MigrationTenantName ?? '—'}</TableCell>
                                        <TableCell align={"center"}>
                                            <Tooltip title={"Migration-Tenant zuweisen"}>
                                                <IconButton size={"small"} onClick={() => handleEditClick(employee)}>
                                                    <SwapHorizIcon fontSize={"small"}/>
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {paged.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align={"center"} sx={{color: 'text.secondary'}}>
                                            Keine Mitarbeiter gefunden.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={filtered.length}
                            page={page}
                            onPageChange={(e, p) => setPage(p)}
                            rowsPerPage={50}
                            rowsPerPageOptions={[50]}
                            labelDisplayedRows={({from, to, count}) => `${from}–${to} von ${count}`}
                        />
                    </TableContainer>
                )}
            </Box>

            <Dialog open={editEmployee !== null} onClose={handleDialogClose} maxWidth="xs" fullWidth>
                <DialogTitle>Migration-Tenant zuweisen</DialogTitle>
                <DialogContent>
                    {editEmployee && (
                        <Box sx={{pt: 1}}>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                {editEmployee.M_Vorname} {editEmployee.M_Name} (GID: {editEmployee['M_Pers-Nr']})
                            </Typography>
                            <RadioGroup
                                value={selectedTenantId}
                                onChange={(e) => setSelectedTenantId(e.target.value)}
                            >
                                <FormControlLabel
                                    value="none"
                                    control={<Radio/>}
                                    label="Keine Zuordnung"
                                />
                                {tenants.map(tenant => (
                                    <FormControlLabel
                                        key={tenant.T_ID}
                                        value={String(tenant.T_ID)}
                                        control={<Radio/>}
                                        label={tenant.T_Name}
                                    />
                                ))}
                            </RadioGroup>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} style={{color: "gray"}}>Abbrechen</Button>
                    <Button
                        onClick={handleSave}
                        color="primary"
                        disabled={saving}
                    >
                        {saving ? <CircularProgress size={20}/> : 'Speichern'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={() => setSnackbarOpen(false)}
                      anchorOrigin={{vertical: "top", horizontal: "right"}} TransitionComponent={Grow}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{width: '100%'}}>
                    {snackbarText}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default AdminEmployeesPage;
